import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
/* eslint-disable  import/no-cycle */
import keyBy from 'lodash/keyBy';
import set from 'lodash/set';
import { RootState } from 'store/rootReducer';
import { ApiErrors, Loaders } from 'api/types';
import { UNAUTHORIZED, FORBIDDEN, NOT_FOUND } from 'http-status-codes';
import { i18n } from 'helpers/i18n';
import { axiosInstance } from '../axiosInstance';

export type Customer = {
  address: string;
  city: string;
  company_name: string;
  created_at?: string;
  deleted_at?: string;
  email: string;
  id: string;
  phone_number: string;
  tax_code: string;
  updated_at?: string;
  zip_code: string;
};

type CustomerState = {
  apiErrors: ApiErrors;
  isSearch?: boolean;
  list: {
    [key: string]: Customer;
  };
  loaders: Loaders;
  pagination: {
    index: number;
    indexesCount: number;
    size: number;
  };
  searchTerm: string;
};

export interface UpdateCustomersListPayload {
  customers: Customer[];
  isSearch: boolean;
  pagination?: {
    index: number;
    indexesCount: number;
    size: number;
  };
  searchTerm: string;
}

const customersApiPrefix = '/contacts';

// TODO: use object instead of  array
const initialState: CustomerState = {
  apiErrors: {
    loadingError: '',
    savingError: ''
  },
  isSearch: false,
  list: {},
  loaders: {
    isSaving: false,
    isDeletingItems: {}
  },
  pagination: {
    size: 30,
    index: 1,
    indexesCount: 0
  },
  searchTerm: ''
};

export const fetchCustomers = createAsyncThunk<UpdateCustomersListPayload, any, { rejectValue: string }>(
  'customers/fetchCustomers',
  async (params, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(`${customersApiPrefix}`, params);
      const { data } = response;
      const isSearch = false;
      /*
      if (get(params, 'search')) {
        isSearch = true;
      } 
      */

      return {
        customers: data,
        isSearch,
        searchTerm: ''
      };
    } catch (err) {
      if (!err.response) {
        return rejectWithValue(i18n.t('errors.something went wrong'));
      }
      const { status } = err.response;
      if (status === UNAUTHORIZED || status === FORBIDDEN) {
        return rejectWithValue(i18n.t('errors.unauthorized'));
      }
      return rejectWithValue(i18n.t('errors.something went wrong'));
    }
  }
);

export const deleteCustomer = createAsyncThunk<string, string, { rejectValue: string }>(
  'customers/deleteCustomer',
  async (id, { dispatch, rejectWithValue }) => {
    try {
      dispatch(updateListDelete({ id, value: true }));
      await axiosInstance.delete(`${customersApiPrefix}/${id}`);
      return id;
    } catch (err) {
      if (!err.response) {
        return rejectWithValue(i18n.t('errors.something went wrong'));
      }
      const { status } = err.response;
      switch (status) {
        case FORBIDDEN:
        case UNAUTHORIZED:
          return rejectWithValue(i18n.t('customers.you cannot delete this user'));
        case NOT_FOUND:
          return rejectWithValue(i18n.t('customers.not found'));
        default:
          return rejectWithValue(i18n.t('errors.something went wrong'));
      }
    } finally {
      dispatch(updateListDelete({ id, value: false }));
    }
  }
);

const customersSlice = createSlice({
  initialState,
  name: 'customers',
  reducers: {
    closeError: state => {
      state.apiErrors = {};
    },
    updateListDelete: (state, { payload }: PayloadAction<{ id: string; value: boolean }>) => {
      const { id, value } = payload;
      if (value) {
        set(state, `loaders.deletingErrorItems.${id}`, '');
      }
      set(state, `loaders.isDeletingItems.${id}`, value);
    },
    resetCustomers: state => initialState
  },
  extraReducers: builder =>
    builder
      .addCase(fetchCustomers.fulfilled, (state, { payload }) => {
        const { customers, /* pagination, */ isSearch, searchTerm } = payload;
        state.isSearch = isSearch;
        /* state.pagination = pagination; */
        state.searchTerm = searchTerm;
        state.loaders.isLoading = false;
        state.list = keyBy(customers, 'id');
      })
      .addCase(fetchCustomers.pending, state => {
        state.loaders.isLoading = true;
        state.apiErrors.loadingError = '';
      })
      .addCase(fetchCustomers.rejected, (state, { payload }) => {
        state.loaders.isLoading = false;
        state.apiErrors.loadingError = payload || i18n.t('errors.something went wrong');
      })
      .addCase(deleteCustomer.fulfilled, (state, { payload }) => {
        delete state.list[`${payload}`];
      })
});

const { actions, reducer } = customersSlice;
export { reducer as customers };
export const { closeError, resetCustomers, updateListDelete } = actions;

export const getCustomers = (state: RootState) => state.customers.list;
export const getCustomersLoaders = (state: RootState) => state.customers.loaders;
export const getCustomersApiErrors = (state: RootState) => state.customers.apiErrors;
export const getCustomersPagination = (state: RootState) => state.customers.pagination;
